<template>
  <div v-if="hasPermission('standardsFamilies.view')">
    <v-skeleton-loader
      v-if="loading"
      class="m-auto"
      type="table"
      transition="scale-transition"
    />
    <TableRemote
      v-else
      :iconHeader="hasPermission('standardsFamilies.add') ? 'add' : null"
      :title="$t('standardFamilies.standardFamilies')"
      :isSelectable="hasPermission('standards.view')"
      :items="displayedStandardFamilies"
      :pagination="pagination"
      :headers="tableHeaders"
      hideDefaultFooter
      :isInfinitScroll="true"
      :itemActions="actions"
      :isCustomIcon="true"
      :sortableColumns="['name']"
      :centerColumns="['isTeamDefault', 'action']"
      :notSortableColumns="['isTeamDefault', 'action']"
      data-cy="productTable"
      @sendQueryParams="fetchQuery"
      @send-item="redirect"
      @iconAction="onClickAction"
    >
      <template v-slot:[`item.isTeamDefault`]="{ item }">
        <span
          class="dot"
          :class="item.isTeamDefault ? 'red' : null"
        />
      </template>
      <template #withInfiniteScroll>
        <div v-intersect="endIntersect" />
      </template>
    </TableRemote>
  </div>
  <div v-else>
    <NoPermissionAlert :message="$t('permissions.error')" />
  </div>
</template>

<script>
import Vue from 'vue';
import { mapGetters } from 'vuex';
import ConfirmForm from '@/components/shared/widgets/ConfirmForm.vue';
import TableRemote from '@/components/shared/content-views/VTableRemote';
import StandardFamilyForm from '@/components/forms/StandardFamilyForm.vue';
import NoPermissionAlert from '@/components/shared/NoPermissionAlert';
import perpage from '@/constants/pagination';

export default {
    components: {
        TableRemote,
        NoPermissionAlert,
    },
    data() {
        return {
            loading: false,
            standardFamilies: [],
            defaultStandardFamily: null,
            pagination: {},
            queryParams: { perpage, order: 'name_asc' },
        };
    },
    computed: {
        ...mapGetters([
            'teamID',
            'hasPermission',
        ]),
        tableHeaders() {
            return [
                'name',
                'isTeamDefault',
            ].map(header => ({
                text: this.$t(`standardFamilies.labels.${header}`),
                value: header,
            }));
        },
        displayedStandardFamilies() {
            return this.standardFamilies.map(({ id, name }) => ({
                id,
                name,
                isTeamDefault: this.isTeamDefault(id),
            }));
        },
        actions() {
            const actions = [];

            if (this.hasPermission('standardsFamilies.edit')) {
                actions.push({
                    name: this.$t('forms.btn.edit'),
                    icon: 'edit',
                });
            }

            if (this.hasPermission('standardsFamilies.remove')) {
                actions.push({
                    name: this.$t('forms.btn.delete'),
                    icon: 'delete',
                    iconAndTextColor: '#EF4135',
                });
            }

            return actions;
        },
    },
    async created() {
        this.loading = true;
        await this.getStandardFamilies();
        this.loading = false;
    },
    methods: {
        async getStandardFamilies() {
            const { data: team } = await this.$rq.getTeam(this.teamID);
            this.defaultStandardFamily = team.defaultStandardFamily;

            const { data } = await this.$rq.getStandardFamilies(this.queryParams);
            this.standardFamilies = [...this.standardFamilies, ...data.standardFamilies];
            this.pagination = data.pagination;
        },
        async fetchQuery(queryParams) {
            this.queryParams = { ...this.queryParams, ...queryParams, page: 1 };
            this.pagination = {};
            this.standardFamilies = [];
            await this.getStandardFamilies();
        },
        redirect(item) {
            this.$router.push({
                name: 'standards',
                params: {
                    standardFamilyId: item.id,
                },
            });
        },
        isTeamDefault(standardFamilyId) {
            if (!this.defaultStandardFamily) {
                return false;
            }

            return standardFamilyId === this.defaultStandardFamily;
        },
        onClickAdd() {
            this.$root.$emit('showModal', {
                title: this.$t('standardFamilies.addStandardFamily'),
                component: Vue.component('addFamilyStandardForm', StandardFamilyForm),
                onConfirm: requestPayload => this.addStandardFamily(requestPayload),
            });
        },
        onClickAction(item, action) {
            if (item === 'add') {
                this.onClickAdd();
            }
            if (action.icon === 'edit') {
                this.$root.$emit('showModal', {
                    title: this.$t('standardFamilies.editStandardFamily'),
                    component: Vue.component('editFamilyStandardForm', StandardFamilyForm),
                    payload: {
                        standardFamily: {
                            ...item,
                            isTeamDefault: this.isTeamDefault(item.id),
                        },
                        isEdition: true,
                    },
                    onConfirm: async requestPayload => this.update(item, requestPayload),
                });
            } else if (action.icon === 'delete') {
                this.$root.$emit('showModal', {
                    title: this.$t('standardFamilies.removeStandardFamily'),
                    component: Vue.component('confirmFamilyStandardForm', ConfirmForm),
                    payload: { msg: this.$t('standardFamilies.confirmRemoveStandardFamily') },
                    onConfirm: () => this.deleteStandardFamily(item),
                    isForm: false,
                });
            }
        },
        async addStandardFamily(requestPayload) {
            const { data } = await this.$rq.addStandardFamily(requestPayload);
            this.$toasted.global.toast_success({
                message: this.$t('msg.standardFamilyCreated', { data: data.name }),
            });
            this.fetchQuery({});
        },
        async update(item, requestPayload) {
            const promises = [];

            if (requestPayload.isTeamDefault !== this.isTeamDefault(item.id)) {
                promises.push(this.$rq.patchTeam(
                    this.teamID,
                    { defaultStandardFamily: requestPayload.isTeamDefault ? item.id : null },
                ));
            }

            if (item.name !== requestPayload.name) {
                promises.push(this.updateStandardFamily(item.id, requestPayload));
            }

            await Promise.all(promises);
            this.fetchQuery({});
        },
        async updateStandardFamily(standardFamilyId, requestPayload) {
            const { data } = await this.$rq.updateStandardFamily(standardFamilyId, requestPayload);
            this.$toasted.global.toast_success({
                message: this.$t('msg.standardFamilyModified', { data: data.name }),
            });
            this.fetchQuery({});
        },
        async deleteStandardFamily(requestPayload) {
            await this.$rq.deleteStandardFamily(requestPayload.id);
            this.$toasted.global.toast_success({
                message: this.$t('msg.standardFamilyRemoved', { data: requestPayload.name }),
            });
            this.fetchQuery({});
        },
        async endIntersect(_, __, isIntersecting) {
            const condition = this.pagination.currentPage < this.pagination.totalPageCount;
            if (isIntersecting && condition) {
                this.queryParams.page = this.pagination.currentPage + 1;
                await this.getStandardFamilies();
                const ref = this.$refs?.tableRemoteRef?.$refs?.tableRemote;
                if (ref?.onScroll) {
                    ref.onScroll();
                }
            }
        },
    },
};
</script>
